/**
 * Attachment & Garment Integrity Engine
 *
 * Renderer-independent physics contract for separating the animated body from
 * clothes, rigid equipment and carried/detached objects. Consumers convert the
 * returned local offsets/angles to their own scene graph implementation.
 *
 * This engine never edits skin weights, bind matrices or a skeleton.
 */

export type AttachmentPhysicsMode =
  | 'body-fitted'
  | 'rigid-socket'
  | 'secondary-garment'
  | 'world-detached'

export type AttachmentSlot =
  | 'helmet'
  | 'hair'
  | 'mask'
  | 'hood'
  | 'necklace'
  | 'shoulderLeft'
  | 'shoulderRight'
  | 'cape'
  | 'chest'
  | 'sleeveLeft'
  | 'sleeveRight'
  | 'gloveLeft'
  | 'gloveRight'
  | 'bracerLeft'
  | 'bracerRight'
  | 'ringLeft'
  | 'ringRight'
  | 'belt'
  | 'tail'
  | 'legs'
  | 'boots'
  | 'backAccessory'
  | 'wingLeft'
  | 'wingRight'
  | 'weaponRight'
  | 'weaponLeft'
  | 'shield'
  | string

export interface AttachmentVec3 {
  x: number
  y: number
  z: number
}

export interface AttachmentIntegrityProfile {
  mode: AttachmentPhysicsMode
  stiffness: number
  damping: number
  inertiaScale: number
  gravityScale: number
  windScale: number
  maxOffset: number
  maxAngle: number
  collisionRadius: number
  bodyClearance: number
  /** Maximum linear correction per second. Guards dt spikes/teleports. */
  maxCorrectionSpeed: number
}

export interface AttachmentIntegrityInput {
  dt: number
  /** Socket-space linear acceleration of the owner. */
  localAcceleration?: Partial<AttachmentVec3>
  /** Socket-space wind/air impulse. */
  localWind?: Partial<AttachmentVec3>
  /** 0..1 normalized grounded/contact confidence. */
  contact01?: number
  /** 0..1 animation transition blend from FrameSynchronizationEngine. */
  frameBlend01?: number
}

export interface AttachmentIntegrityFrame {
  offset: AttachmentVec3
  velocity: AttachmentVec3
  rotation: AttachmentVec3
  mode: AttachmentPhysicsMode
  substeps: number
  clamped: boolean
  finite: boolean
}

const ZERO=():AttachmentVec3=>({x:0,y:0,z:0})
const finite=(n:number|undefined,fallback=0)=>typeof n==='number'&&Number.isFinite(n)?n:fallback
const clamp=(n:number,a:number,b:number)=>Math.max(a,Math.min(b,finite(n)))
const length=(v:AttachmentVec3)=>Math.hypot(v.x,v.y,v.z)

function clampMagnitude(v:AttachmentVec3,max:number):boolean{
  const l=length(v)
  if(!(l>max)||l<1e-9)return false
  const k=max/l
  v.x*=k;v.y*=k;v.z*=k
  return true
}

function defaults(mode:AttachmentPhysicsMode):AttachmentIntegrityProfile{
  if(mode==='body-fitted')return{
    mode,stiffness:120,damping:24,inertiaScale:0,gravityScale:0,windScale:0,
    maxOffset:.006,maxAngle:.015,collisionRadius:.03,bodyClearance:.004,maxCorrectionSpeed:.4,
  }
  if(mode==='rigid-socket')return{
    mode,stiffness:180,damping:32,inertiaScale:0,gravityScale:0,windScale:0,
    maxOffset:.003,maxAngle:.008,collisionRadius:.04,bodyClearance:.006,maxCorrectionSpeed:.5,
  }
  if(mode==='world-detached')return{
    mode,stiffness:22,damping:7,inertiaScale:.5,gravityScale:1,windScale:.35,
    maxOffset:8,maxAngle:Math.PI,collisionRadius:.06,bodyClearance:0,maxCorrectionSpeed:18,
  }
  return{
    mode,stiffness:46,damping:10.5,inertiaScale:.12,gravityScale:.08,windScale:.18,
    maxOffset:.18,maxAngle:.24,collisionRadius:.05,bodyClearance:.018,maxCorrectionSpeed:2.2,
  }
}

const SECONDARY=new Set([
  'cape','hair','tail','backAccessory','wingLeft','wingRight','necklace',
])
const RIGID=new Set([
  'weaponRight','weaponLeft','shield','ringLeft','ringRight',
])
const FITTED=new Set([
  'helmet','mask','hood','shoulderLeft','shoulderRight','chest','sleeveLeft','sleeveRight',
  'gloveLeft','gloveRight','bracerLeft','bracerRight','belt','legs','boots',
])

export function attachmentModeForSlot(slot:AttachmentSlot):AttachmentPhysicsMode{
  if(SECONDARY.has(slot))return 'secondary-garment'
  if(RIGID.has(slot))return 'rigid-socket'
  if(FITTED.has(slot))return 'body-fitted'
  return 'rigid-socket'
}

export function attachmentProfileForSlot(
  slot:AttachmentSlot,
  overrides?:Partial<AttachmentIntegrityProfile>,
):AttachmentIntegrityProfile{
  const mode=overrides?.mode??attachmentModeForSlot(slot)
  return{...defaults(mode),...(overrides??{}),mode}
}

export class AttachmentIntegrityRuntime{
  readonly profile:AttachmentIntegrityProfile
  readonly identity:string
  readonly slot:AttachmentSlot
  private offset=ZERO()
  private velocity=ZERO()
  private rotation=ZERO()

  constructor(
    identity:string,
    slot:AttachmentSlot,
    profile?:Partial<AttachmentIntegrityProfile>,
  ){
    this.identity=identity
    this.slot=slot
    this.profile=attachmentProfileForSlot(slot,profile)
  }

  reset():void{
    this.offset=ZERO()
    this.velocity=ZERO()
    this.rotation=ZERO()
  }

  snapshot():AttachmentIntegrityFrame{
    return{
      offset:{...this.offset},velocity:{...this.velocity},rotation:{...this.rotation},
      mode:this.profile.mode,substeps:0,clamped:false,finite:true,
    }
  }

  step(input:AttachmentIntegrityInput):AttachmentIntegrityFrame{
    const rawDt=finite(input.dt)
    const dt=Math.max(0,Math.min(1/15,rawDt>0?rawDt:0))
    const substeps=dt>0?Math.max(1,Math.ceil(dt/(1/120))):0
    const h=substeps>0?dt/substeps:0
    const p=this.profile
    let clamped=false

    // Fitted equipment is intentionally almost rigid: it gets an independent
    // container but no inertial sway that could drive it through the body.
    if(p.mode==='body-fitted'){
      const decay=Math.exp(-p.damping*dt)
      this.offset.x*=decay;this.offset.y*=decay;this.offset.z*=decay
      this.velocity.x*=decay;this.velocity.y*=decay;this.velocity.z*=decay
      this.rotation.x*=decay;this.rotation.y*=decay;this.rotation.z*=decay
      return{
        offset:{...this.offset},velocity:{...this.velocity},rotation:{...this.rotation},
        mode:p.mode,substeps,clamped:false,finite:true,
      }
    }

    const acceleration:AttachmentVec3={
      x:finite(input.localAcceleration?.x),
      y:finite(input.localAcceleration?.y),
      z:finite(input.localAcceleration?.z),
    }
    const wind:AttachmentVec3={
      x:finite(input.localWind?.x),
      y:finite(input.localWind?.y),
      z:finite(input.localWind?.z),
    }
    const contact=clamp(input.contact01??1,0,1)
    const blend=clamp(input.frameBlend01??1,0,1)
    // During an animation transition, reduce external secondary impulses until
    // the body pose has converged. This prevents a cape/weapon from reacting
    // to both the old and new pose in one visual frame.
    const impulseGate=.18+.82*blend

    for(let i=0;i<substeps;i++){
      const target:AttachmentVec3={
        x:-acceleration.x*p.inertiaScale*impulseGate+wind.x*p.windScale,
        y:-acceleration.y*p.inertiaScale*impulseGate+wind.y*p.windScale-p.gravityScale*(1-contact),
        z:-acceleration.z*p.inertiaScale*impulseGate+wind.z*p.windScale,
      }
      clampMagnitude(target,p.maxOffset)

      const ax=(target.x-this.offset.x)*p.stiffness-this.velocity.x*p.damping
      const ay=(target.y-this.offset.y)*p.stiffness-this.velocity.y*p.damping
      const az=(target.z-this.offset.z)*p.stiffness-this.velocity.z*p.damping
      this.velocity.x+=ax*h;this.velocity.y+=ay*h;this.velocity.z+=az*h

      const maxStep=p.maxCorrectionSpeed*h
      const dx=this.velocity.x*h,dy=this.velocity.y*h,dz=this.velocity.z*h
      const stepLen=Math.hypot(dx,dy,dz)
      const k=stepLen>maxStep&&stepLen>1e-9?maxStep/stepLen:1
      if(k<1)clamped=true
      this.offset.x+=dx*k;this.offset.y+=dy*k;this.offset.z+=dz*k

      if(clampMagnitude(this.offset,p.maxOffset))clamped=true

      // Orientation follows offset direction, independently from the body's
      // skinning. This is small-angle secondary motion, never bone mutation.
      const rx=clamp(-this.offset.z*1.8,-p.maxAngle,p.maxAngle)
      const ry=clamp(this.offset.x*.8,-p.maxAngle,p.maxAngle)
      const rz=clamp(this.offset.x*1.8,-p.maxAngle,p.maxAngle)
      const rotAlpha=1-Math.exp(-Math.max(8,p.stiffness*.22)*h)
      this.rotation.x+=(rx-this.rotation.x)*rotAlpha
      this.rotation.y+=(ry-this.rotation.y)*rotAlpha
      this.rotation.z+=(rz-this.rotation.z)*rotAlpha
    }

    const isFinite=[
      this.offset.x,this.offset.y,this.offset.z,this.velocity.x,this.velocity.y,this.velocity.z,
      this.rotation.x,this.rotation.y,this.rotation.z,
    ].every(Number.isFinite)
    if(!isFinite)this.reset()

    return{
      offset:{...this.offset},
      velocity:{...this.velocity},
      rotation:{...this.rotation},
      mode:p.mode,
      substeps,
      clamped,
      finite:isFinite,
    }
  }
}
